import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, Dimensions, Alert, ScrollView } from 'react-native';
import Carousel from 'pinar';
import { useNavigation } from '@react-navigation/native';
import useSuggestedComboStore from '../../store/useSuggestedComboStore';
import styles from './styles';
import { useBagStore } from '../../store/BagStore';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ProductsService from '../../services/ProductsService/ProductsService';
import { useUserStore } from '../../store/UserStore';
import { Image } from 'react-native';
import { Images } from '../../constants/Images';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import AxiosService from '../../services/AxiosService';
import { withDebounce } from '../../services/AxiosService';
import { showSuccess } from '../../utils/globalAlert';

// Tipos para melhorar a legibilidade e evitar erros
type ComboProduct = {
  sku?: string;
  product_sku?: string;
  quantity: number;
  catalog_item?: any;
  operation?: string;
  discountedPrice?: number;
  preco?: number;
  [key: string]: any;
};

type ComboData = {
  combo_sku: string;
  similaridade: number;
  detalhes?: ComboProduct[];
  itens?: ComboProduct[];
  itens_removidos?: Record<string, number>;
  quantidade_combo?: number;
  combo?: any;
  catalog_combo?: any;
  index?: number;
  [key: string]: any;
};

const SuggestedCombo: React.FC = () => {
  // Store global
  const {
    loading: globalLoading,
    setLoading: setGlobalLoading,
    toCompleteCombos,
    completedCombos,
    originalCombos,
    clearAll,
    setToCompleteCombos,
    setCompletedCombos,
    setOriginalCombos,
    setCombos,
  } = useSuggestedComboStore();

  const navigation = useNavigation();
  const products = useBagStore(state => state.products);
  const { addProduct, updateProductQuantity } = useBagStore();
  const { user } = useUserStore();

  // Estados locais - simplificados e com propósito claro
  const [loading, setLoading] = useState(false); // Estado de loading local para operações dentro do componente
  const [combos, setCombosState] = useState<ComboData[]>([]); // Combos já formatados e prontos para exibição
  const [productCache, setProductCache] = useState<Record<string, any>>({}); // Cache de produtos carregados
  const [refreshKey, setRefreshKey] = useState(0); // Chave para forçar re-renderização do carousel

  useEffect(() => {
    const shouldNavigateToCheckout = toCompleteCombos.length === 0 &&
      completedCombos.length === 0 &&
      !globalLoading &&
      !loading;

    if (shouldNavigateToCheckout) {
      navigation.navigate('Checkout');
    }
  }, [toCompleteCombos, completedCombos, globalLoading, loading, navigation]);

  // Função para buscar os combos sugeridos da API
  const getSuggestedCombosBase = useCallback(async (clientCode: string, cartProducts: any[]) => {
    if (!clientCode || !cartProducts || cartProducts.length === 0) {
      return null;
    }

    // Ativa o loading global
    setGlobalLoading(true);
    setLoading(true);

    try {
      const instance = AxiosService.createProductsAxiosInstance();

      // Garantir que temos dados válidos para enviar
      const data = cartProducts.map(product => ({
        sku: product.sku || '',
        quantity: product.quantity || 0,
      })).filter(item => item.sku !== '');

      const response = await instance.post(
        '/verifyCart',
        { items: data },
        {
          headers: {
            'client-code': clientCode,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response?.data?.data) {
        console.warn('[getSuggestedCombos] Resposta sem dados:', response);
        throw new Error('No data received from server');
      }

      // Atualiza os dados na store global
      const responseData = response.data.data;

      // Limpe estados DEPOIS de ter os dados novos
      clearAll();
      setCombosState([]);

      setCombos(responseData);
      setOriginalCombos(responseData?.combos_detalhados || {});
      setCompletedCombos(responseData?.combos_aplicaveis || []);
      setToCompleteCombos(responseData?.combos_similares || []);

      return responseData;
    } catch (error) {
      return null;
    } finally {
      setGlobalLoading(false);
      setLoading(false);
    }
  }, [clearAll, setCombos, setOriginalCombos, setCompletedCombos, setToCompleteCombos, setGlobalLoading]);

  // Aplica o debounce à função base
  const getSuggestedCombos = useCallback(
    withDebounce(getSuggestedCombosBase, 300),
    [getSuggestedCombosBase]
  );
  // Função para carregar um produto pelo SKU
  const loadProduct = useCallback(async (sku: string): Promise<any | null> => {
    if (!sku) return null;

    // Retorna do cache se disponível
    if (productCache[sku]) {
      return productCache[sku];
    }

    try {
      const response = await ProductsService.catalogProductsGet(
        user?.clientCode,
        1,
        1,
        undefined,
        undefined,
        sku
      );

      const product = response.data?.products?.[0] || null;

      if (product) {
        // Atualiza o cache de produtos
        setProductCache(prev => ({ ...prev, [sku]: product }));
      }

      return product;
    } catch (error) {
      console.error(`[loadProduct] Erro ao carregar produto ${sku}:`, error);
      return null;
    }
  }, [user?.clientCode, productCache]);

  // Efeito para processar os combos quando toCompleteCombos ou products mudarem
  useEffect(() => {
    if (!toCompleteCombos.length) {
      setCombosState([]);
      return;
    }

    const processCombos = async () => {
      setLoading(true);

      try {
        // Ordena os combos por similaridade
        const sortedCombos = [...toCompleteCombos].sort((a, b) => b.similaridade - a.similaridade);

        // Formata os dados do combo e adiciona os itens do carrinho
        const formattedCombos = sortedCombos.map((combo, index) => ({
          ...combo,
          index: index + 1,
          itens: products.filter(product =>
            combo.detalhes?.some(detail => detail.product_sku === product.sku)
          ),
        }));

        // Cria um array para armazenar os combos processados
        const processedCombos: ComboData[] = [];

        // Processa cada combo para carregar os detalhes do produto do combo
        for (const combo of formattedCombos) {
          // Carrega os dados do produto do combo
          const comboProduct = await loadProduct(combo.combo_sku);

          if (comboProduct) {
            // Adiciona o produto ao combo
            processedCombos.push({
              ...combo,
              combo: comboProduct
            });
          }
        }

        // Atualiza o estado com os combos processados
        setCombosState(processedCombos);
        // Força uma atualização do carousel
        setRefreshKey(prev => prev + 1);
      } finally {
        setLoading(false);
      }
    };

    processCombos();
  }, [toCompleteCombos, products, loadProduct]);

  // Componente de item do combo (memoizado para prevenir re-renderizações)
  const ComboItem = React.memo(({
    item,
    product = null,
    fatorDesconto = 0,
    removidos = null,
    quantidade_combo = null,
    original = false
  }) => {
    let displayProduct = product;
    if (!displayProduct) {
      displayProduct = item.catalog_item || null;
    }

    if (!displayProduct) {
      return (
        <View style={styles.itemContainer}>
          <Text style={styles.itemText}>Carregando...</Text>
          <ActivityIndicator size="small" color="#E91E63" />
        </View>
      );
    }

    const precoOriginal = displayProduct?.preco || displayProduct?.PRECO;
    const precoComDesconto = (precoOriginal - (precoOriginal * fatorDesconto))
      ?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return (
      <View style={styles.itemContainer}>
        <Image
          source={displayProduct.image ? { uri: displayProduct.image } : Images.emptyImgIcon}
          style={styles.itemImage}
        />
        <View style={styles.textContainer}>
          <Text style={styles.itemText}>
            {((displayProduct?.descricao || displayProduct?.DESCRICAO)?.replace(/\n/g, '') + ' ' +
              (displayProduct?.complemento || displayProduct?.COMPLEMENTO || '')
                .replace(/\\n/g, '')
                .replace(/\n/g, '').split(')')[0] + ')'
            )}
          </Text>

          {item?.operation === 'ZS03' && (
            <View style={styles.freeBadge}>
              <Text style={styles.freeText}>GRÁTIS</Text>
            </View>
          )}

          <Text style={styles.itemText}>
            Quantidade: {removidos?.[item?.sku] || (parseInt(item.quantity, 10) * Math.max(1, quantidade_combo || 1)) || 0}
          </Text>

          {!(item?.operation === 'ZS03') &&
            <Text style={(original ? styles.itemText : styles.priceOriginal)}>
              Preço da caixa: {original
                ? (item.discountedPrice || item?.preco)?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                : precoOriginal?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
              }
            </Text>
          }

          {(!original && !(item?.operation === 'ZS03')) &&
            <Text style={styles.priceDiscounted}>
              Nesse combo: {precoComDesconto}
            </Text>
          }
        </View>
      </View>
    );
  });

  const SkeletonLoader = () => (
    <View style={styles.skeletonContainer}>
      <View style={styles.skeletonTitle} />
      <View style={styles.skeletonSubtitle} />

      {/* Itens a serem trocados */}
      <View style={[styles.skeletonSectionTitle, styles.skeletonSeparator]} />

      {[1, 2].map((_, index) => (
        <View key={`item-${index}`} style={styles.skeletonItemContainer}>
          <View style={styles.skeletonItemImage} />
          <View style={{ flex: 1 }}>
            <View style={styles.skeletonTextLong} />
            <View style={styles.skeletonTextShort} />
            <View style={styles.skeletonPrice} />
          </View>
        </View>
      ))}

      <View style={styles.skeletonSubtotalText} />

      {/* Pelo combo */}
      <View style={styles.skeletonSectionTitle} />
      <View style={styles.skeletonDescription} />

      {/* Itens do combo */}
      {[1, 2].map((_, index) => (
        <View key={`combo-${index}`} style={styles.skeletonItemContainer}>
          <View style={styles.skeletonItemImage} />
          <View style={{ flex: 1 }}>
            <View style={styles.skeletonTextLong} />
            <View style={styles.skeletonTextShort} />
            <View style={styles.skeletonDiscountPrice} />
          </View>
        </View>
      ))}

      <View style={styles.skeletonSubtotalText} />
      <View style={styles.skeletonButton} />
    </View>
  );

  // Renderiza um card de combo
  const renderComboCard = useCallback(({ item, index }) => {
    if (!item.combo) {
      return <SkeletonLoader />;
    }

    const itensValidos = item.itens || item.detalhes || originalCombos[item.combo_sku] || [];

    // Filtra itens duplicados por SKU
    const uniqueItensValidos = itensValidos.reduce((acc: ComboProduct[], product) => {
      const sku = product.sku || product.product_sku;
      if (sku && !acc.some(i => (i.sku || i.product_sku) === sku)) {
        acc.push(product);
      }
      return acc;
    }, []);

    // Consolida os itens por SKU para somar quantidades
    const consolidateItemsBySku = (items: ComboProduct[]) => {
      return items.reduce((acc: ComboProduct[], item) => {
        const sku = item.sku || item.product_sku;
        if (!sku) return acc;

        const existingItem = acc.find((i) => (i.sku === sku || i.product_sku === sku));
        if (existingItem) {
          existingItem.quantity += item.quantity;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, []);
    };

    const consolidatedItens = consolidateItemsBySku(uniqueItensValidos);

    // Cálculo dos subtotais
    const subtotalProdutos = consolidatedItens
      .filter(product => !product?.operation || product?.operation !== 'ZS03')
      .reduce((sum, product) => {
        const productPrice = product.discountedPrice || product.preco || product?.catalog_item?.PRECO || 0;
        return sum + product.quantity * productPrice;
      }, 0);

    const subtotalCombos = (item.detalhes || originalCombos[item.combo_sku])?.reduce(
      (sum, product) => {
        if (product?.operation === 'ZS03') return sum;
        const productPrice = product.discountedPrice || product.preco || product?.catalog_item?.PRECO || 0;
        return sum + product.quantity * productPrice;
      }, 0) || 0;

    const diferenca = subtotalCombos - (item.combo.preco || item.catalog_combo?.PRECO || 0);

    // Função para aplicar o combo
    const handleApplyCombo = async () => {
      setLoading(true);

      try {
        const comboProduct = item.combo;
        if (comboProduct) {
          const quantidadeCombo = Number(item.quantidade_combo) || 1;
          const limiteCombo = Number(comboProduct?.qtdeLimite);

          const quantityToAdd = isNaN(limiteCombo)
            ? quantidadeCombo
            : Math.max(1, Math.min(quantidadeCombo, limiteCombo));

          // Cria uma cópia para não modificar o objeto original
          const productToAdd = { ...comboProduct, quantity: quantityToAdd };
          addProduct(productToAdd);
        }

        // Processa os itens a serem removidos
        if (item.itens_removidos && Object.keys(item.itens_removidos).length > 0) {
          Object.keys(item.itens_removidos).forEach(sku => {
            const removedQuantity = item.itens_removidos[sku];
            const productInCart = products.find(product => product.sku === sku);
            if (productInCart) {
              updateProductQuantity(sku, productInCart.quantity - removedQuantity);
            }
          });
        } else if (item.detalhes?.length > 0) {
          item.detalhes.forEach(detail => {
            const productInCart = products.find(product => product.sku === detail.product_sku);
            if (productInCart) {
              updateProductQuantity(detail.product_sku, productInCart.quantity - detail.quantity);
            }
          });
        }

        // Mostra a confirmação ao usuário
        showSuccess('Combo aplicado!', 'Os itens foram atualizados no seu carrinho.');

        // Verifica se é o único combo disponível
        const isOnlyComboAvailable = combos.length === 1;

        if (isOnlyComboAvailable) {
          // Se for o único combo, navegamos IMEDIATAMENTE para o checkout
          // sem fazer nenhuma outra operação
          setLoading(false); // Desativa o loading antes de navegar
          navigation.navigate('Checkout');
          return; // Retorno antecipado para não executar o resto da função
        }

        // Para múltiplos combos, mantenha o loading ativo enquanto busca novos combos
        // Não limpe os combos atuais ainda para evitar o flash de "nenhum combo"

        // Busca os produtos atualizados do carrinho
        const currentProducts = useBagStore.getState().products;

        // Busca novos combos atualizados
        const newCombos = await getSuggestedCombos(user?.clientCode, currentProducts);

        // Só agora limpe o cache se novos combos foram carregados com sucesso
        if (newCombos) {
          setProductCache({});
        }
      } finally {
      }
    };
    return (
      <View key={index} style={styles.card}>
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          <Text style={[styles.sectionTitle, styles.sectionSeparator]}>Troque os itens:</Text>

          {/* Lista de itens a serem trocados */}
          {item.itens
            .filter((product: ComboProduct, idx: number, self: ComboProduct[]) =>
              idx === self.findIndex(p => p.sku === product.sku)
            )
            .map((product: ComboProduct, productIndex: number) => (
              <ComboItem
                key={`toTrade-${product.sku || ''}-${productIndex}`}
                item={product}
                product={item.itens.find(p => p.sku === product.sku)}
                removidos={item?.itens_removidos}
                original
              />
            ))}

          <Text style={styles.subtotalText}>
            Subtotal: {subtotalProdutos?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Text>

          <Text style={styles.sectionTitle}>Pelo combo:</Text>
          <Text>{item?.combo?.descricao}</Text>

          {/* Lista de itens do combo */}
          {(item.detalhes || originalCombos[item?.combo_sku]) &&
            (item.detalhes || originalCombos[item?.combo_sku]).map((detail: ComboProduct, detailIndex: number) => (
              <ComboItem
                key={`traded-${detail.product_sku || detail.sku || ''}-${detailIndex}`}
                item={detail}
                product={detail.catalog_item}
                fatorDesconto={diferenca / subtotalCombos}
                quantidade_combo={item.quantidade_combo}
              />
            ))}

          <Text style={styles.subtotalText}>
            Subtotal do Combo: {(item.combo.preco * Math.max(1, item?.quantidade_combo || 1))
              ?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || '0,00'}
          </Text>

          <ButtonComponent
            title="Aplicar Combo"
            onPress={handleApplyCombo}
          />
        </ScrollView>
      </View>
    );
  }, [addProduct, getSuggestedCombos, originalCombos, products, updateProductQuantity, user?.clientCode]);

  return (
    <View style={styles.container}>
      <SearchHeader onPress={() => navigation.goBack()} title="" />
      <Text style={styles.header}>💡 Combos sugeridos para você:</Text>

      {/* Condicional para exibir o estado correto */}
      {globalLoading || loading ? (
        // Skeleton loader em vez de ActivityIndicator
        <View style={{ flex: 1 }}>
          <Carousel
            loop={false}
            showsControls={false}
            controlsButtonStyle={styles.controlsButton}
            dotStyle={styles.dotStyle}
            activeDotStyle={styles.activeDotStyle}
          >
            {/* Mostra 3 skeleton loaders para simular múltiplos combos sendo carregados */}
            {[1, 2, 3].map((_, idx) => (
              <View key={`skeleton-${idx}`} style={styles.card}>
                <SkeletonLoader />
              </View>
            ))}
          </Carousel>
        </View>
      ) : combos.length === 0 ? (
        <View style={{ alignItems: 'center', marginTop: 32 }}>
          <Text style={{ color: '#888', fontSize: 16 }}>
            Nenhum combo sugerido no momento.
          </Text>
        </View>
      ) : (
        <Carousel
          key={refreshKey} // Força re-renderização quando os dados mudam
          loop
          showsControls={false}
          controlsButtonStyle={styles.controlsButton}
          dotStyle={styles.dotStyle}
          activeDotStyle={styles.activeDotStyle}
        >
          {combos.map((item, index) => renderComboCard({ item, index }))}
        </Carousel>
      )}

      <View style={styles.buttonContainer}>
        <ButtonComponent
          title="Pular e continuar"
          onPress={() => navigation.navigate('Checkout')}
          width="90%"
          outlined
        />
      </View>
    </View>
  );
};

export default SuggestedCombo;